import { Spinner } from '@/components/ui';
import { useSessionWatcher } from '@/hooks/use-session-watcher';
import { AppLayout } from '@/layouts/AppLayout';
import { AdminUsersPage } from '@/pages/AdminUsersPage';
import { BacklogPage } from '@/pages/BacklogPage';
import { BoardPage } from '@/pages/BoardPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { ChatPage } from '@/pages/ChatPage';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { SprintHistoryPage } from '@/pages/SprintHistoryPage';
import { TeamPage } from '@/pages/TeamPage';
import { useAuth } from '@/stores/auth';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

export function App() {
  const restore = useAuth((state) => state.restore);
  const initializing = useAuth((state) => state.initializing);

  useSessionWatcher();

  useEffect(() => {
    void restore();
  }, [restore]);

  if (initializing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <Spinner />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/registro"
        element={
          <PublicOnly>
            <RegisterPage />
          </PublicOnly>
        }
      />
      <Route
        path="/cambiar-contrasena"
        element={
          <RequireAuth allowPasswordChange>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/tablero" replace />} />
        <Route path="/tablero" element={<BoardPage />} />
        <Route path="/backlog" element={<BacklogPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/equipo" element={<TeamPage />} />
        <Route path="/proyectos" element={<ProjectsPage />} />
        <Route path="/historial" element={<SprintHistoryPage />} />
        <Route
          path="/admin/usuarios"
          element={
            <RequireAdmin>
              <AdminUsersPage />
            </RequireAdmin>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Puerta de entrada. Ademas de exigir sesion, desvia al cambio de contrasena
 * obligatorio: mientras `mustChangePassword` siga en true el backend rechaza el
 * resto de rutas, asi que no tiene sentido dejar navegar.
 */
function RequireAuth({
  children,
  allowPasswordChange = false,
}: {
  children: React.ReactNode;
  allowPasswordChange?: boolean;
}) {
  const user = useAuth((state) => state.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (user.mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/cambiar-contrasena" replace />;
  }
  if (!user.mustChangePassword && allowPasswordChange) {
    return <Navigate to="/tablero" replace />;
  }
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuth((state) => state.user);
  return user?.role === 'ADMIN' ? <>{children}</> : <Navigate to="/tablero" replace />;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const user = useAuth((state) => state.user);
  if (!user) {
    return <>{children}</>;
  }
  return <Navigate to={user.mustChangePassword ? '/cambiar-contrasena' : '/tablero'} replace />;
}
